/**
 * The vault version gate (Standalone vault board, card 1.1).
 *
 * A vault, keyblock or synced item written by a NEWER Apiboo is not damage and
 * not a wrong passphrase. Rust answers `vault_update_required` for it (bare, or
 * as the prefix of a longer drain message), keeps every row it cannot read yet,
 * and never writes over them. The UI's only job is to say "update Apiboo" and
 * to offer the update — never "that passphrase did not work".
 */
import { useUpdateStore } from '../store/updateStore';

export const VAULT_UPDATE_REQUIRED = 'vault_update_required';

/** True for the Rust token, bare or as a message prefix ("vault_update_required: …"). */
export function isVaultUpdateRequired(e) {
  const raw = typeof e === 'string' ? e : String(e?.message || e || '');
  const text = raw.trim();
  return text === VAULT_UPDATE_REQUIRED || text.startsWith(`${VAULT_UPDATE_REQUIRED}:`);
}

/**
 * A user-initiated update check, exactly like Settings → "Check for updates":
 * it bypasses the once-a-day cache and is not counted as an active device.
 * When an update is on offer the app's own update dialog opens.
 *
 * @returns {Promise<'available'|'none'|'error'>}
 */
export async function checkForAppUpdateNow() {
  try {
    // Loaded on click, not at import: the vault screens that use this module
    // are imported all over the app, and updateService pulls in the Tauri app
    // API, which nothing on those paths otherwise needs.
    const { checkForUpdates } = await import('./updateService');
    const result = await checkForUpdates({ ignoreDismissed: true, force: true, counted: false });
    const store = useUpdateStore.getState();
    store.setUpdateInfo(result);
    if (result?.updateAvailable) {
      store.setUpdateError('');
      store.setUpdateModalOpen(true);
      return 'available';
    }
    return result?.error ? 'error' : 'none';
  } catch {
    return 'error';
  }
}
